import React, { useState, useEffect, useCallback } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faSave, 
  faTimes, 
  faPlus, 
  faTrash, 
  faCalendarAlt,
  faClock,
  faUser,
  faMapMarkerAlt,
  faBox,
  faDollarSign,
  faSpinner,
  faCheck
} from '@fortawesome/free-solid-svg-icons';
import { collection, addDoc, doc, updateDoc, getDocs, query, where } from 'firebase/firestore';
import { db } from '../../firebase';
import { getProducts } from '../../services/productService';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import TermsModal from '../checkout/TermsModal';

const ManualOrderForm = ({ order = null, onClose, onSave }) => {
  const [formData, setFormData] = useState({
    // Customer Information
    customer: {
      name: '',
      email: '',
      phone: ''
    },
    // Order Items
    items: [{
      id: '',
      name: '',
      price: 0,
      quantity: 1
    }],
    // Dates and Times
    rentalDate: null,
    startTime: '10:00 AM',
    endTime: '6:00 PM',
    // Location and Delivery
    location: 'Elkhart Office',
    address: {
      street: '',
      city: '',
      state: '',
      zip: ''
    },
    // Pricing
    subtotal: 0,
    tax: 0,
    damageWaiver: 0,
    travelFee: 0,
    miscFees: 0,
    overnightDelivery: false,
    overnightUpchargeCost: 0,
    totalAmount: 0,
    amountPaid: 0,
    amountDue: 0,
    // Order Details
    status: 'confirmed',
    paymentStatus: 'partial',
    paymentMethod: 'cash',
    customerComments: '',
    // System Fields
    source: 'manual-entry',
    orderNumber: '',
    autodraft: false
  });

  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState({});
  const [showTermsModal, setShowTermsModal] = useState(false);
  
  // Add waiver state management
  const [liabilityWaiver, setLiabilityWaiver] = useState({
    termsAgreed: false,
    purchaseDamageWaiver: false,
    surfaceType: '',
    powerWithin50Feet: false,
    overnightUpcharge: false
  });

  // Available time slots
  const timeSlots = [
    '8:00 AM', '9:00 AM', '10:00 AM', '11:00 AM', '12:00 PM',
    '1:00 PM', '2:00 PM', '3:00 PM', '4:00 PM', '5:00 PM', '6:00 PM', '7:00 PM'
  ];

  // Define loadProducts function first
  const loadProducts = useCallback(async () => {
    try {
      setLoading(true);
      const allProducts = await getProducts();
      setProducts(allProducts);
    } catch (error) {
      console.error('Error loading products:', error);
      setProducts([]); // Set empty array on error
    } finally {
      setLoading(false);
    }
  }, []); // Remove formData.location dependency

  // Load products and populate form if editing
  useEffect(() => {
    loadProducts();
    if (order) {
      populateFormFromOrder(order);
    } else {
      generateOrderNumber();
    }
  }, [order, loadProducts]);

  // Calculate totals when relevant fields change
  const calculateTotals = useCallback(() => {
    const subtotal = formData.items.reduce((sum, item) => sum + (item.price * item.quantity), 0);
    
    // Calculate damage waiver from liability waiver (10% of subtotal)
    const damageWaiverAmount = liabilityWaiver.purchaseDamageWaiver ? subtotal * 0.1 : 0;
    
    // Calculate overnight upcharge from liability waiver
    const overnightUpcharge = liabilityWaiver.overnightUpcharge ? 100 : 0;
    
    // Calculate tax on all taxable components: subtotal + damage waiver + travel fee + misc fees + overnight upcharge
    const taxableAmount = subtotal + damageWaiverAmount + formData.travelFee + formData.miscFees + overnightUpcharge;
    const tax = taxableAmount * 0.07; // 7% tax on all taxable components
    const totalAmount = subtotal + tax + damageWaiverAmount + formData.travelFee + formData.miscFees + overnightUpcharge;
    const amountDue = totalAmount - formData.amountPaid;

    setFormData(prev => ({
      ...prev,
      subtotal,
      tax,
      damageWaiver: damageWaiverAmount,
      overnightDelivery: liabilityWaiver.overnightUpcharge,
      overnightUpchargeCost: overnightUpcharge,
      totalAmount,
      amountDue
    }));
  }, [formData.items, formData.travelFee, formData.miscFees, formData.amountPaid, liabilityWaiver.purchaseDamageWaiver, liabilityWaiver.overnightUpcharge]);

  // Trigger calculation when dependencies change
  useEffect(() => {
    calculateTotals();
  }, [calculateTotals]);

  const populateFormFromOrder = (orderData) => {
    // Handle address format - support both old string format and new object format
    let addressObj = { street: '', city: '', state: '', zip: '' };
    if (orderData.address) {
      if (typeof orderData.address === 'string') {
        // Parse old string format
        const parts = orderData.address.split(',').map(part => part.trim());
        addressObj.street = parts[0] || '';
        addressObj.city = parts[1] || '';
        if (parts[2]) {
          const stateZip = parts[2].split(' ');
          addressObj.state = stateZip[0] || '';
          addressObj.zip = stateZip[1] || '';
        }
      } else {
        // Use new object format
        addressObj = orderData.address;
      }
    }

    setFormData({
      customer: {
        name: orderData.customer?.name || '',
        email: orderData.customer?.email || '',
        phone: orderData.customer?.phone || ''
      },
      items: orderData.items || [{ id: '', name: '', price: 0, quantity: 1 }],
      rentalDate: orderData.rentalDate ? new Date(orderData.rentalDate) : null,
      startTime: orderData.startTime || '10:00 AM',
      endTime: orderData.endTime || '6:00 PM',
      location: orderData.location || 'Elkhart Office',
      address: addressObj,
      subtotal: orderData.subtotal || 0,
      tax: orderData.tax || 0,
      damageWaiver: orderData.damageWaiver || 0,
      travelFee: orderData.travelFee || 0,
      miscFees: orderData.miscFees || 0,
      overnightDelivery: orderData.overnightDelivery || (orderData.pricing?.overnightUpchargeCost > 0) || (orderData.liabilityWaiver?.overnightUpcharge) || false,
      overnightUpchargeCost: orderData.overnightUpchargeCost || orderData.pricing?.overnightUpchargeCost || 0,
      totalAmount: orderData.totalAmount || 0,
      amountPaid: orderData.amountPaid || 0,
      amountDue: orderData.amountDue || 0,
      status: orderData.status || 'confirmed',
      paymentStatus: orderData.paymentStatus || 'partial',
      paymentMethod: orderData.paymentMethod || 'cash',
      customerComments: orderData.customerComments || '',
      source: orderData.source || 'manual-entry',
      orderNumber: orderData.orderNumber || '',
      autodraft: orderData.autodraft || false
    });

    // Populate waiver data if it exists
    if (orderData.liabilityWaiver) {
      setLiabilityWaiver({
        termsAgreed: orderData.liabilityWaiver.termsAgreed || false,
        purchaseDamageWaiver: orderData.liabilityWaiver.purchaseDamageWaiver || false,
        surfaceType: orderData.liabilityWaiver.surfaceType || '',
        powerWithin50Feet: orderData.liabilityWaiver.powerWithin50Feet || false,
        overnightUpcharge: orderData.liabilityWaiver.overnightUpcharge || false
      });
    }
  };

  const generateOrderNumber = async () => {
    try {
      
      // Get ALL orders to check for duplicates across entire system
      const ordersRef = collection(db, 'orders');
      const snapshot = await getDocs(ordersRef);
      
      // Collect all existing order numbers
      const existingOrderNumbers = new Set();
      snapshot.docs.forEach(doc => {
        const orderData = doc.data();
        if (orderData.orderNumber) {
          const num = parseInt(orderData.orderNumber);
          if (!isNaN(num)) {
            existingOrderNumbers.add(num);
          }
        }
      });

      // Find the next available order number starting from 1500 for manual orders
      let nextOrderNumber = 1500;
      
      // If there are existing order numbers, start from the highest + 1
      if (existingOrderNumbers.size > 0) {
        const maxExisting = Math.max(...existingOrderNumbers);
        nextOrderNumber = Math.max(1500, maxExisting + 1);
      }

      // Double-check that our chosen number doesn't exist (safety check)
      while (existingOrderNumbers.has(nextOrderNumber)) {
        nextOrderNumber++;
      }


      setFormData(prev => ({
        ...prev,
        orderNumber: nextOrderNumber.toString()
      }));

      return nextOrderNumber;
    } catch (error) {
      console.error('Error generating order number:', error);
      
      // Fallback: generate a random number and ensure it's unique
      let fallbackNumber;
      let attempts = 0;
      const maxAttempts = 10;
      
      do {
        fallbackNumber = Math.floor(Math.random() * 9000 + 1500); // Random between 1500-10499
        attempts++;
      } while (attempts < maxAttempts);

      
      setFormData(prev => ({
        ...prev,
        orderNumber: fallbackNumber.toString()
      }));

      return fallbackNumber;
    }
  };

  const handleInputChange = (field, value) => {
    if (field.includes('.')) {
      const [parent, child] = field.split('.');
      setFormData(prev => ({
        ...prev,
        [parent]: {
          ...prev[parent],
          [child]: value
        }
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [field]: value
      }));
    }
  };

  // Add handler for liability waiver inputs
  const handleLiabilityWaiverChange = (e) => {
    const { name, value, checked, type } = e.target;
    setLiabilityWaiver({
      ...liabilityWaiver,
      [name]: type === 'checkbox' ? checked : value
    });
  };

  const handleItemChange = (index, field, value) => {
    const updatedItems = [...formData.items];
    if (field === 'product') {
      // When product is selected, update id, name, and price
      const selectedProduct = products.find(p => p.id === value);
      updatedItems[index] = {
        ...updatedItems[index],
        id: value,
        name: selectedProduct?.name || '',
        price: selectedProduct?.price || 0
      };
    } else {
      updatedItems[index][field] = value;
    }
    setFormData(prev => ({ ...prev, items: updatedItems }));
  };

  const addItem = () => {
    setFormData(prev => ({
      ...prev,
      items: [...prev.items, { id: '', name: '', price: 0, quantity: 1 }]
    }));
  };

  const removeItem = (index) => {
    if (formData.items.length > 1) {
      const updatedItems = formData.items.filter((_, i) => i !== index);
      setFormData(prev => ({ ...prev, items: updatedItems }));
    }
  };

  // Send webhook for NEW manual order creation only
  // NOTE: Webhooks should ONLY be sent for new orders, not for updates/edits
  // This prevents duplicate notifications and maintains data integrity
  const sendManualOrderWebhook = async (orderData) => {
    try {
      const webhookUrl = 'https://services.leadconnectorhq.com/hooks/FynKHfyY9oPrJ1ojOFxi/webhook-trigger/8ac544e3-f241-4e81-9115-f617719a20d1';
      
      // Prepare the webhook payload for NEW manual orders only
      const webhookPayload = {
        // Order Information
        orderId: orderData.paymentIntentId || `manual-order-${orderData.orderNumber}`,
        orderNumber: orderData.orderNumber,
        orderDate: new Date().toISOString(),
        orderSource: 'Manual Entry - Admin',
        isManualOrder: true,
        
        // Customer Information
        customer: {
          firstName: orderData.customer.name.split(' ')[0] || '',
          lastName: orderData.customer.name.split(' ').slice(1).join(' ') || '',
          fullName: orderData.customer.name,
          email: orderData.customer.email || '',
          phone: orderData.customer.phone || ''
        },
        
        // Delivery Information
        delivery: {
          address: orderData.address.street,
          city: orderData.address.city,
          state: orderData.address.state,
          zipCode: orderData.address.zip,
          fullAddress: orderData.addressFormatted,
          deliveryDate: orderData.rentalDate,
          deliveryTime: orderData.startTime,
          specialInstructions: orderData.customerComments || '',
          servingBranch: orderData.location,
          deliveryDistance: 0 // Manual orders don't calculate distance
        },
        
        // Product Information
        items: orderData.items.map(item => ({
          id: item.id,
          name: item.name,
          price: item.price,
          quantity: item.quantity,
          rentalDate: orderData.rentalDate,
          rentalTime: orderData.startTime,
          isAddon: false
        })),
        
        // Financial Information
        pricing: {
          subtotal: orderData.subtotal,
          deliveryFee: orderData.travelFee,
          taxAmount: orderData.tax,
          damageWaiver: orderData.damageWaiver,
          miscFees: orderData.miscFees,
          totalAmount: orderData.totalAmount,
          amountPaid: orderData.amountPaid,
          amountDue: orderData.amountDue,
          currency: 'USD'
        },
        
        // Payment Information
        payment: {
          method: orderData.paymentMethod,
          status: orderData.paymentStatus,
          paid: orderData.amountPaid,
          due: orderData.amountDue
        },
        
        // Order Status
        status: {
          orderStatus: orderData.status,
          paymentStatus: orderData.paymentStatus,
          isAutodraft: orderData.autodraft
        },
        
        // Business Information
        business: {
          companyName: 'Rowe Bros Entertainment',
          orderSource: 'Manual Entry - Admin Dashboard',
          orderType: 'Manual Order Created',
          timestamp: new Date().toISOString(),
          adminUser: 'Admin Dashboard User' // You can modify this to include actual admin user if available
        }
      };
      
      
      const webhookResponse = await fetch(webhookUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify(webhookPayload)
      });
      
      if (webhookResponse.ok) {
      } else {
        console.error('Failed to send manual order webhook:', webhookResponse.status, webhookResponse.statusText);
      }
      
    } catch (error) {
      console.error('Error sending manual order webhook:', error);
      // Don't throw error to prevent interrupting the order save flow
    }
  };

  const validateForm = () => {
    const newErrors = {};


    if (!formData.customer.name.trim()) {
      newErrors['customer.name'] = 'Customer name is required';
    }

    if (!formData.customer.phone.trim()) {
      newErrors['customer.phone'] = 'Customer phone is required';
    }

    if (!formData.rentalDate) {
      newErrors.rentalDate = 'Rental date is required';
    }

    if (formData.items.some(item => !item.id || !item.name)) {
      newErrors.items = 'All items must have a valid product selected';
    }

    if (!formData.location.trim()) {
      newErrors.location = 'Location is required';
    }

    // Validate individual address fields
    if (!formData.address.street.trim()) {
      newErrors['address.street'] = 'Street address is required';
    }

    if (!formData.address.city.trim()) {
      newErrors['address.city'] = 'City is required';
    }

    if (!formData.address.state.trim()) {
      newErrors['address.state'] = 'State is required';
    }

    if (!formData.address.zip.trim()) {
      newErrors['address.zip'] = 'ZIP code is required';
    }

    // Validate waiver fields
    if (!liabilityWaiver.termsAgreed) {
      newErrors['liabilityWaiver.termsAgreed'] = 'Terms and conditions must be agreed to';
    }

    if (!liabilityWaiver.surfaceType.trim()) {
      newErrors['liabilityWaiver.surfaceType'] = 'Surface type is required';
    }

    if (liabilityWaiver.powerWithin50Feet === '' || liabilityWaiver.powerWithin50Feet === null) {
      newErrors['liabilityWaiver.powerWithin50Feet'] = 'Power outlet question must be answered';
    }

    setErrors(newErrors);
    
    const isValid = Object.keys(newErrors).length === 0;
    
    if (!isValid) {
      // Scroll to first error
      setTimeout(() => {
        const firstErrorElement = document.querySelector('.error');
        if (firstErrorElement) {
          firstErrorElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
      }, 100);
    }
    
    return isValid;
  };

  const checkForDuplicateOrderNumber = async (orderNumber) => {
    try {
      const ordersRef = collection(db, 'orders');
      const q = query(ordersRef, where('orderNumber', '==', orderNumber));
      const snapshot = await getDocs(q);
      
      // If we're editing an existing order, exclude it from the duplicate check
      if (order) {
        return snapshot.docs.some(doc => doc.id !== order.id);
      }
      
      return snapshot.docs.length > 0;
    } catch (error) {
      console.error('Error checking for duplicate order number:', error);
      return false; // Assume no duplicate if check fails
    }
  };

  const handleSave = async () => {
    
    if (!validateForm()) {
      alert('Please fill in all required fields to continue.');
      return;
    }

    setSaving(true);
    
    try {
      // Check for duplicate order number before saving
      const isDuplicate = await checkForDuplicateOrderNumber(formData.orderNumber);
      if (isDuplicate) {
        alert('Order number already exists. Generating a new one...');
        await generateOrderNumber();
        setSaving(false);
        return;
      }

      const orderData = {
        ...formData,
        // Format address as both object and formatted string for compatibility
        address: formData.address,
        addressFormatted: `${formData.address.street}, ${formData.address.city}, ${formData.address.state} ${formData.address.zip}`,
        rentalDate: formData.rentalDate ? formData.rentalDate.toISOString().split('T')[0] : null,
        createdAt: order ? order.createdAt : new Date(),
        updatedAt: new Date(),
        paymentIntentId: `manual-order-${formData.orderNumber}`,
        // Include liability waiver data
        liabilityWaiver: liabilityWaiver
      };


      let savedOrderId = null;
      const isNewOrder = !order;

      if (order) {
        // Update existing order
        
        await updateDoc(doc(db, 'orders', order.id), orderData);
        savedOrderId = order.id;
        alert('Order updated successfully!');
      } else {
        // Create new order
        const docRef = await addDoc(collection(db, 'orders'), orderData);
        savedOrderId = docRef.id;
        alert('Order created successfully!');
      }

      // Send webhook ONLY for new manual order creation (not for updates)
      if (isNewOrder) {
        try {
          await sendManualOrderWebhook(orderData);
        } catch (webhookError) {
          console.error('Webhook failed (but order was saved):', webhookError);
          // Don't fail the entire save if webhook fails
        }
      } else {
      }

      onSave && onSave();
      onClose();
    } catch (error) {
      console.error('Error saving order:', error);
      console.error('Error details:', {
        message: error.message,
        code: error.code,
        stack: error.stack
      });
      
      let errorMessage = 'Error saving order. ';
      if (error.code === 'permission-denied') {
        errorMessage += 'You do not have permission to update orders.';
      } else if (error.code === 'not-found') {
        errorMessage += 'Order not found in database.';
      } else if (error.message) {
        errorMessage += error.message;
      } else {
        errorMessage += 'Please try again.';
      }
      
      alert(errorMessage);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="manual-order-overlay">
      <div className="manual-order-form">
        <div className="form-header">
          <h2>
            <FontAwesomeIcon icon={faBox} />
            {order ? 'Edit Order' : 'Create Manual Order'}
          </h2>
          <button className="close-button" onClick={onClose}>
            <FontAwesomeIcon icon={faTimes} />
          </button>
        </div>

        <div className="form-content">
          {/* Error Summary */}
          {Object.keys(errors).length > 0 && (
            <div className="error-summary">
              <h4>Please fix the following errors:</h4>
              <ul>
                {Object.entries(errors).map(([field, message]) => (
                  <li key={field}>
                    <strong>{field.replace(/\./g, ' ').replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase())}:</strong> {message}
                  </li>
                ))}
              </ul>
            </div>
          )}
          
          {/* Customer Information */}
          <section className="form-section">
            <h3><FontAwesomeIcon icon={faUser} /> Customer Information</h3>
            <div className="form-grid">
              <div className="form-group">
                <label>Customer Name *</label>
                <input
                  type="text"
                  value={formData.customer.name}
                  onChange={(e) => handleInputChange('customer.name', e.target.value)}
                  className={errors['customer.name'] ? 'error' : ''}
                />
                {errors['customer.name'] && <span className="error-text">{errors['customer.name']}</span>}
              </div>
              <div className="form-group">
                <label>Email</label>
                <input
                  type="email"
                  value={formData.customer.email}
                  onChange={(e) => handleInputChange('customer.email', e.target.value)}
                />
              </div>
              <div className="form-group">
                <label>Phone *</label>
                <input
                  type="tel"
                  value={formData.customer.phone}
                  onChange={(e) => handleInputChange('customer.phone', e.target.value)}
                  className={errors['customer.phone'] ? 'error' : ''}
                />
                {errors['customer.phone'] && <span className="error-text">{errors['customer.phone']}</span>}
              </div>
            </div>
          </section>

          {/* Order Items */}
          <section className="form-section">
            <h3><FontAwesomeIcon icon={faBox} /> Order Items</h3>
            {formData.items.map((item, index) => (
              <div key={index} className="item-row">
                <div className="form-grid">
                  <div className="form-group">
                    <label>Product *</label>
                    <select
                      value={item.id}
                      onChange={(e) => handleItemChange(index, 'product', e.target.value)}
                      className={errors.items ? 'error' : ''}
                      disabled={loading}
                    >
                      <option value="">
                        {loading ? 'Loading products...' : 'Select Product'}
                      </option>
                      {products.length === 0 && !loading && (
                        <option value="" disabled>
                          No products available
                        </option>
                      )}
                      {products.map(product => (
                        <option key={product.id} value={product.id}>
                          {product.name} - ${product.price}/day
                        </option>
                      ))}
                    </select>
                    {products.length === 0 && !loading && (
                      <span className="info-text">
                        No products found. Please check your Firebase products collection.
                      </span>
                    )}
                  </div>
                  <div className="form-group">
                    <label>Quantity</label>
                    <input
                      type="number"
                      min="1"
                      value={item.quantity}
                      onChange={(e) => handleItemChange(index, 'quantity', parseInt(e.target.value) || 1)}
                    />
                  </div>
                  <div className="form-group">
                    <label>Price</label>
                    <input
                      type="number"
                      step="0.01"
                      value={item.price}
                      onChange={(e) => handleItemChange(index, 'price', parseFloat(e.target.value) || 0)}
                    />
                  </div>
                  <div className="form-group">
                    <label>Total</label>
                    <span className="calculated-value">${(item.price * item.quantity).toFixed(2)}</span>
                  </div>
                </div>
                <div className="item-actions">
                  {formData.items.length > 1 && (
                    <button
                      type="button"
                      className="remove-item-button"
                      onClick={() => removeItem(index)}
                    >
                      <FontAwesomeIcon icon={faTrash} />
                    </button>
                  )}
                </div>
              </div>
            ))}
            {errors.items && <span className="error-text">{errors.items}</span>}
            <button type="button" className="add-item-button" onClick={addItem}>
              <FontAwesomeIcon icon={faPlus} /> Add Item
            </button>
          </section>

          {/* Date and Time */}
          <section className="form-section">
            <h3><FontAwesomeIcon icon={faCalendarAlt} /> Date & Time</h3>
            <div className="form-grid">
              <div className="form-group">
                <label>Rental Date *</label>
                <DatePicker
                  selected={formData.rentalDate}
                  onChange={(date) => handleInputChange('rentalDate', date)}
                  dateFormat="MMMM d, yyyy"
                  className={errors.rentalDate ? 'error' : ''}
                  placeholderText="Select rental date"
                />
                {errors.rentalDate && <span className="error-text">{errors.rentalDate}</span>}
              </div>
              <div className="form-group">
                <label>Delivery Time</label>
                <select
                  value={formData.startTime}
                  onChange={(e) => handleInputChange('startTime', e.target.value)}
                >
                  {timeSlots.map(time => (
                    <option key={time} value={time}>{time}</option>
                  ))}
                </select>
              </div>
              <div className="form-group">
                <label>Pickup Time</label>
                <select
                  value={formData.endTime}
                  onChange={(e) => handleInputChange('endTime', e.target.value)}
                >
                  {timeSlots.map(time => (
                    <option key={time} value={time}>{time}</option>
                  ))}
                </select>
              </div>
            </div>
          </section>

          {/* Location */}
          <section className="form-section">
            <h3><FontAwesomeIcon icon={faMapMarkerAlt} /> Location & Delivery</h3>
            <div className="form-grid">
              <div className="form-group">
                <label>Location *</label>
                <select
                  value={formData.location}
                  onChange={(e) => handleInputChange('location', e.target.value)}
                  className={errors.location ? 'error' : ''}
                >
                  <option value="Angola Office">Angola Office</option>
                  <option value="Elkhart Office">Elkhart Office</option>
                </select>
                {errors.location && <span className="error-text">{errors.location}</span>}
              </div>
              <div className="form-group full-width">
                <label>Customer Delivery Address *</label>
                <div className="address-fields">
                  <div className="form-group">
                    <label>Street Address *</label>
                    <input
                      type="text"
                      value={formData.address.street || ''}
                      onChange={(e) => handleInputChange('address.street', e.target.value)}
                      placeholder="Enter street address..."
                      className={errors['address.street'] ? 'error' : ''}
                    />
                    {errors['address.street'] && <span className="error-text">{errors['address.street']}</span>}
                  </div>
                  <div className="address-row">
                    <div className="form-group">
                      <label>City *</label>
                      <input
                        type="text"
                        value={formData.address.city || ''}
                        onChange={(e) => handleInputChange('address.city', e.target.value)}
                        placeholder="City..."
                        className={errors['address.city'] ? 'error' : ''}
                      />
                      {errors['address.city'] && <span className="error-text">{errors['address.city']}</span>}
                    </div>
                    <div className="form-group">
                      <label>State *</label>
                      <select
                        value={formData.address.state || ''}
                        onChange={(e) => handleInputChange('address.state', e.target.value)}
                        className={errors['address.state'] ? 'error' : ''}
                      >
                        <option value="">Select State</option>
                        <option value="IN">Indiana</option>
                        <option value="MI">Michigan</option>
                        <option value="OH">Ohio</option>
                        <option value="IL">Illinois</option>
                      </select>
                      {errors['address.state'] && <span className="error-text">{errors['address.state']}</span>}
                    </div>
                    <div className="form-group">
                      <label>ZIP Code *</label>
                      <input
                        type="text"
                        value={formData.address.zip || ''}
                        onChange={(e) => handleInputChange('address.zip', e.target.value)}
                        placeholder="ZIP..."
                        className={errors['address.zip'] ? 'error' : ''}
                        maxLength="10"
                />
                      {errors['address.zip'] && <span className="error-text">{errors['address.zip']}</span>}
                    </div>
                  </div>
                </div>
                <span className="help-text">
                  Enter the complete delivery address where equipment should be delivered
                </span>
              </div>
            </div>
          </section>

          {/* Pricing */}
          <section className="form-section">
            <h3><FontAwesomeIcon icon={faDollarSign} /> Pricing</h3>
            <div className="form-grid">
              <div className="form-group">
                <label>Subtotal</label>
                <span className="calculated-value">${formData.subtotal.toFixed(2)}</span>
              </div>
              <div className="form-group">
                <label>Tax (7%)</label>
                <span className="calculated-value">${formData.tax.toFixed(2)}</span>
              </div>
              <div className="form-group">
                <label>Damage Waiver</label>
                <input
                  type="number"
                  step="0.01"
                  value={formData.damageWaiver}
                  onChange={(e) => handleInputChange('damageWaiver', parseFloat(e.target.value) || 0)}
                />
              </div>
              <div className="form-group">
                <label>Travel Fee</label>
                <input
                  type="number"
                  step="0.01"
                  value={formData.travelFee}
                  onChange={(e) => handleInputChange('travelFee', parseFloat(e.target.value) || 0)}
                />
              </div>
              <div className="form-group">
                <label>Misc Fees</label>
                <input
                  type="number"
                  step="0.01"
                  value={formData.miscFees}
                  onChange={(e) => handleInputChange('miscFees', parseFloat(e.target.value) || 0)}
                />
              </div>
              <div className="form-group">
                <label>
                  <input
                    type="checkbox"
                    checked={formData.overnightDelivery}
                    onChange={(e) => handleInputChange('overnightDelivery', e.target.checked)}
                    style={{ marginRight: '8px' }}
                  />
                  Overnight Delivery (+$100)
                </label>
                <span className="calculated-value">${formData.overnightUpchargeCost.toFixed(2)}</span>
              </div>
              <div className="form-group">
                <label>Total Amount</label>
                <span className="calculated-value total">${formData.totalAmount.toFixed(2)}</span>
              </div>
              <div className="form-group">
                <label>Amount Paid</label>
                <input
                  type="number"
                  step="0.01"
                  value={formData.amountPaid}
                  onChange={(e) => handleInputChange('amountPaid', parseFloat(e.target.value) || 0)}
                />
              </div>
              <div className="form-group">
                <label>Amount Due</label>
                <span className={`calculated-value ${formData.amountDue > 0 ? 'due' : 'paid'}`}>
                  ${formData.amountDue.toFixed(2)}
                </span>
              </div>
            </div>
          </section>

          {/* Waiver & Terms Section */}
          <section className="form-section">
            <h3>Waiver & Terms</h3>
            <div className="form-grid">
              <div className="form-group full-width">
                <div className="checkbox-container" style={{ 
                  display: 'flex',
                  alignItems: 'center',
                  gap: '10px',
                  padding: '12px',
                  border: `2px solid ${errors['liabilityWaiver.termsAgreed'] ? '#f44336' : '#ddd'}`,
                  borderRadius: '8px',
                  background: '#fff'
                }}>
                  <input
                    type="checkbox"
                    name="termsAgreed"
                    checked={liabilityWaiver.termsAgreed}
                    onChange={handleLiabilityWaiverChange}
                    style={{ 
                      width: '18px', 
                      height: '18px',
                      marginRight: '8px'
                    }}
                    className={errors['liabilityWaiver.termsAgreed'] ? 'error' : ''}
                  />
                  <label style={{ margin: 0, fontSize: '14px', fontWeight: '500' }}>
                    I agree to the{' '}
                    <button
                      type="button"
                      className="terms-link"
                      onClick={() => setShowTermsModal(true)}
                      style={{ 
                        background: 'none',
                        border: 'none',
                        color: '#007bff',
                        textDecoration: 'underline',
                        cursor: 'pointer',
                        padding: 0,
                        fontSize: '14px',
                        fontWeight: '500'
                      }}
                    >
                      Terms and Conditions
                    </button>
                    {' '}*
                  </label>
                </div>
                {errors['liabilityWaiver.termsAgreed'] && <span className="error-text">{errors['liabilityWaiver.termsAgreed']}</span>}
              </div>
              <div className="form-group">
                <label>Surface Type *</label>
                <select
                  name="surfaceType"
                  value={liabilityWaiver.surfaceType}
                  onChange={handleLiabilityWaiverChange}
                  className={errors['liabilityWaiver.surfaceType'] ? 'error' : ''}
                >
                  <option value="">Select Surface Type</option>
                  <option value="grass">Grass</option>
                  <option value="concrete">Concrete</option>
                  <option value="asphalt">Asphalt</option>
                  <option value="dirt">Dirt</option>
                  <option value="sand">Sand</option>
                  <option value="other">Other</option>
                </select>
                {errors['liabilityWaiver.surfaceType'] && <span className="error-text">{errors['liabilityWaiver.surfaceType']}</span>}
              </div>
              <div className="form-group full-width">
                <label>Power Outlet Within 100 Feet *</label>
                <div className="outlet-question-buttons" style={{ 
                  display: 'flex', 
                  gap: '10px', 
                  marginTop: '8px' 
                }}>
                  <button
                    type="button"
                    className={`outlet-btn ${liabilityWaiver.powerWithin50Feet === true ? 'active yes' : ''}`}
                    onClick={() => setLiabilityWaiver(prev => ({ ...prev, powerWithin50Feet: true }))}
                    style={{
                      padding: '12px 20px',
                      border: '2px solid #ddd',
                      borderRadius: '8px',
                      background: liabilityWaiver.powerWithin50Feet === true ? '#4caf50' : '#fff',
                      color: liabilityWaiver.powerWithin50Feet === true ? '#fff' : '#333',
                      cursor: 'pointer',
                      fontSize: '14px',
                      fontWeight: '500',
                      transition: 'all 0.3s ease',
                      flex: 1
                    }}
                  >
                    <FontAwesomeIcon icon={faCheck} style={{ marginRight: '8px' }} />
                    Yes, I have an outlet within 100 feet
                  </button>
                  <button
                    type="button"
                    className={`outlet-btn ${liabilityWaiver.powerWithin50Feet === false ? 'active no' : ''}`}
                    onClick={() => setLiabilityWaiver(prev => ({ ...prev, powerWithin50Feet: false }))}
                    style={{
                      padding: '12px 20px',
                      border: '2px solid #ddd',
                      borderRadius: '8px',
                      background: liabilityWaiver.powerWithin50Feet === false ? '#f44336' : '#fff',
                      color: liabilityWaiver.powerWithin50Feet === false ? '#fff' : '#333',
                      cursor: 'pointer',
                      fontSize: '14px',
                      fontWeight: '500',
                      transition: 'all 0.3s ease',
                      flex: 1
                    }}
                  >
                    <FontAwesomeIcon icon={faTimes} style={{ marginRight: '8px' }} />
                    No, I don't have an outlet within 100 feet
                  </button>
                </div>
                {errors['liabilityWaiver.powerWithin50Feet'] && <span className="error-text">{errors['liabilityWaiver.powerWithin50Feet']}</span>}
              </div>
              <div className="form-group full-width">
                <div className="checkbox-container" style={{ 
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  padding: '12px',
                  border: '2px solid #ddd',
                  borderRadius: '8px',
                  background: '#fff'
                }}>
                  <label style={{ 
                    display: 'flex', 
                    alignItems: 'center',
                    margin: 0,
                    fontSize: '14px',
                    fontWeight: '500'
                  }}>
                    <input
                      type="checkbox"
                      name="purchaseDamageWaiver"
                      checked={liabilityWaiver.purchaseDamageWaiver}
                      onChange={handleLiabilityWaiverChange}
                      style={{ 
                        width: '18px', 
                        height: '18px',
                        marginRight: '8px'
                      }}
                    />
                    Purchase Damage Waiver (10% of subtotal)
                  </label>
                  <span className="calculated-value" style={{ 
                    fontWeight: 'bold',
                    color: liabilityWaiver.purchaseDamageWaiver ? '#4caf50' : '#666',
                    fontSize: '16px'
                  }}>
                    {liabilityWaiver.purchaseDamageWaiver ? 
                      `$${(formData.subtotal * 0.1).toFixed(2)}` : 
                      '$0.00'
                    }
                  </span>
                </div>
              </div>
              <div className="form-group full-width">
                <div className="checkbox-container" style={{ 
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  padding: '12px',
                  border: '2px solid #ddd',
                  borderRadius: '8px',
                  background: '#fff'
                }}>
                  <label style={{ 
                    display: 'flex', 
                    alignItems: 'center',
                    margin: 0,
                    fontSize: '14px',
                    fontWeight: '500'
                  }}>
                    <input
                      type="checkbox"
                      name="overnightUpcharge"
                      checked={liabilityWaiver.overnightUpcharge}
                      onChange={handleLiabilityWaiverChange}
                      style={{ 
                        width: '18px', 
                        height: '18px',
                        marginRight: '8px'
                      }}
                    />
                    Overnight Delivery (+$100)
                  </label>
                  <span className="calculated-value" style={{ 
                    fontWeight: 'bold',
                    color: liabilityWaiver.overnightUpcharge ? '#4caf50' : '#666',
                    fontSize: '16px'
                  }}>
                    {liabilityWaiver.overnightUpcharge ? '$100.00' : '$0.00'}
                  </span>
                </div>
              </div>
            </div>
          </section>

          {/* Order Details */}
          <section className="form-section">
            <h3>Order Details</h3>
            <div className="form-grid">
              <div className="form-group">
                <label>Order Number (Auto-Generated)</label>
                <div className="order-number-field">
                <input
                  type="text"
                  value={formData.orderNumber}
                    readOnly
                    className="auto-generated-field"
                    placeholder="Generating..."
                  />
                  {!order && (
                    <button
                      type="button"
                      className="refresh-order-number-button"
                      onClick={generateOrderNumber}
                      disabled={saving}
                      title="Generate new order number"
                    >
                      🔄
                    </button>
                  )}
                </div>
                <span className="help-text">
                  {order ? 'Order number cannot be changed for existing orders' : 'Automatically generated unique order number'}
                </span>
              </div>
              <div className="form-group">
                <label>Status</label>
                <select
                  value={formData.status}
                  onChange={(e) => handleInputChange('status', e.target.value)}
                >
                  <option value="pending">Pending</option>
                  <option value="confirmed">Confirmed</option>
                  <option value="processing">Processing</option>
                  <option value="completed">Completed</option>
                  <option value="cancelled">Cancelled</option>
                </select>
              </div>
              <div className="form-group">
                <label>Payment Status</label>
                <select
                  value={formData.paymentStatus}
                  onChange={(e) => handleInputChange('paymentStatus', e.target.value)}
                >
                  <option value="pending">Pending</option>
                  <option value="deposit">Deposit</option>
                  <option value="partial">Partial</option>
                  <option value="paid">Paid</option>
                  <option value="refunded">Refunded</option>
                </select>
              </div>
              <div className="form-group">
                <label>Payment Method</label>
                <select
                  value={formData.paymentMethod}
                  onChange={(e) => handleInputChange('paymentMethod', e.target.value)}
                >
                  <option value="cash">Cash</option>
                  <option value="card">Card</option>
                  <option value="check">Check</option>
                  <option value="venmo">Venmo</option>
                  <option value="zelle">Zelle</option>
                </select>
              </div>
              <div className="form-group full-width">
                <label>Customer Comments</label>
                <textarea
                  value={formData.customerComments}
                  onChange={(e) => handleInputChange('customerComments', e.target.value)}
                  placeholder="Any special instructions or comments..."
                  rows="3"
                />
              </div>
            </div>
          </section>
        </div>

        <div className="form-footer">
          <button className="cancel-button" onClick={onClose} disabled={saving}>
            <FontAwesomeIcon icon={faTimes} /> Cancel
          </button>
          <button className="save-button" onClick={handleSave} disabled={saving}>
            {saving ? (
              <>
                <FontAwesomeIcon icon={faSpinner} spin /> Saving...
              </>
            ) : (
              <>
                <FontAwesomeIcon icon={faSave} /> {order ? 'Update' : 'Create'} Order
              </>
            )}
          </button>
        </div>
      </div>
      
      {/* Terms Modal */}
      {showTermsModal && (
        <TermsModal
          isOpen={showTermsModal}
          onClose={() => setShowTermsModal(false)}
        />
      )}
    </div>
  );
};

export default ManualOrderForm; 